import { useEffect, useState, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { mediaUrl } from "@/lib/api";
import { Poster } from "@/components/PosterCanvas";

const variantFor = (transition, d = 0.8) => {
  switch (transition) {
    case "slide-left":
      return {
        initial: { x: "100%", opacity: 0.6 },
        animate: { x: 0, opacity: 1 },
        exit: { x: "-100%", opacity: 0.6 },
        transition: { duration: d, ease: [0.4, 0, 0.2, 1] },
      };
    case "slide-right":
      return {
        initial: { x: "-100%", opacity: 0.6 },
        animate: { x: 0, opacity: 1 },
        exit: { x: "100%", opacity: 0.6 },
        transition: { duration: d, ease: [0.4, 0, 0.2, 1] },
      };
    case "zoom-in":
      return {
        initial: { scale: 1.2, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        exit: { scale: 1, opacity: 0 },
        transition: { duration: d, ease: "easeInOut" },
      };
    case "zoom-out":
      return {
        initial: { scale: 0.85, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        exit: { scale: 1.1, opacity: 0 },
        transition: { duration: d, ease: "easeInOut" },
      };
    case "ken-burns":
      return {
        initial: { scale: 1, opacity: 0 },
        animate: { scale: 1.15, opacity: 1 },
        exit: { scale: 1.15, opacity: 0 },
        transition: { opacity: { duration: d }, scale: { duration: 8, ease: "linear" } },
      };
    case "cross-dissolve":
      return {
        initial: { opacity: 0, zIndex: 2 },
        animate: { opacity: 1, zIndex: 2 },
        exit: { opacity: 0, zIndex: 1, transition: { delay: d, duration: 0.01 } },
        transition: { duration: d, ease: "easeInOut" },
      };
    case "fade":
    default:
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: d, ease: "easeInOut" },
      };
  }
};

export const SlideShow = ({ images = [], playing = true, loop = true, onEnded, showProgress = true }) => {
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  const ordered = [...images].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const count = ordered.length;

  const advance = useCallback(() => {
    setIndex((prev) => {
      const next = prev + 1;
      if (next >= count) {
        if (loop) return 0;
        if (onEnded) onEnded();
        return prev;
      }
      return next;
    });
  }, [count, loop, onEnded]);

  useEffect(() => {
    if (index >= count && count > 0) setIndex(0);
  }, [count, index]);

  const advanceRef = useRef(advance);
  advanceRef.current = advance;
  const durationMs = Math.max(0.5, ordered[Math.min(index, count - 1)]?.duration ?? 5) * 1000;

  useEffect(() => {
    if (!playing || count === 0) return;
    const start = performance.now();
    setProgress(0);

    const tick = (now) => {
      const elapsed = now - start;
      const pct = Math.min(100, (elapsed / durationMs) * 100);
      setProgress(pct);
      if (elapsed >= durationMs) {
        advanceRef.current();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [index, playing, count, durationMs]);

  if (count === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground font-mono-label text-xs">
        Aucune image
      </div>
    );
  }

  const current = ordered[Math.min(index, count - 1)];
  const v = variantFor(current.transition, current.transition_duration ?? 0.8);

  return (
    <div className="absolute inset-0 overflow-hidden bg-black" data-testid="slideshow-stage">
      <AnimatePresence mode="sync">
        <motion.div
          key={current.id + "-" + index}
          className="absolute inset-0 h-full w-full"
          initial={v.initial}
          animate={v.animate}
          exit={v.exit}
          transition={v.transition}
        >
          {current.type === "poster" && current.poster_data ? (
            <Poster data={current.poster_data} className="h-full w-full bg-black" />
          ) : (
            <>
              <img src={mediaUrl(current.url)} alt="" aria-hidden="true" draggable={false}
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-2xl" data-testid="slide-bg-blur" />
              <img src={mediaUrl(current.url)} alt="" draggable={false}
                className="absolute inset-0 h-full w-full object-contain" data-testid="slide-image" />
            </>
          )}
        </motion.div>
      </AnimatePresence>
      {showProgress && (
        <div className="absolute bottom-0 left-0 right-0 z-20 flex gap-1 p-2">
          {ordered.map((img, i) => (
            <div key={img.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/25">
              <div
                className="h-full bg-primary transition-none"
                style={{ width: i < index ? "100%" : i === index ? `${progress}%` : "0%" }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
